import { API_BASE_URL } from './config';
import { apiFetch } from './apiFetch';

export interface CompanyWithStats {
  id: string;
  name: string;
  userCount: number;
  createdAt: string;
  status: string;
}

export interface CompanyDetail {
  company: { id: string; name: string; createdAt: string };
  users: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    isActive: boolean;
    createdAt: string;
  }>;
}

export interface CreateCompanyInput {
  companyName: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
}

export async function listCompanies(): Promise<CompanyWithStats[]> {
  const res = await apiFetch(`${API_BASE_URL}/api/superadmin/companies`, {
    headers: { 'Content-Type': 'application/json' },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed to list companies');
  return data.data.companies;
}

export async function getCompanyDetail(companyId: string): Promise<CompanyDetail> {
  const res = await apiFetch(`${API_BASE_URL}/api/superadmin/companies/${companyId}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed to get company');
  return data.data;
}

export async function createCompany(
  input: CreateCompanyInput,
): Promise<{ companyId: string; adminUserId: string }> {
  const res = await apiFetch(`${API_BASE_URL}/api/superadmin/companies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed to create company');
  return data.data;
}

export async function updateCompanyName(
  companyId: string,
  name: string,
): Promise<void> {
  const res = await apiFetch(`${API_BASE_URL}/api/superadmin/companies/${companyId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed to update company');
}

export interface BackupEntry {
  success: boolean;
  failed: boolean;
  timestamp: string | null;
  message: string;
  stale: boolean;
}

export interface BackupStatus {
  available: boolean;
  healthy?: boolean;
  message?: string;
  postgres: BackupEntry | null;
  minio: BackupEntry | null;
}

export async function getBackupStatus(): Promise<BackupStatus> {
  const res = await apiFetch(`${API_BASE_URL}/api/superadmin/backup-status`, {
    headers: { 'Content-Type': 'application/json' },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed to get backup status');
  return data.data;
}

export async function addUserToCompany(
  companyId: string,
  input: { name: string; email: string; password: string; role: string },
): Promise<{ userId: string; email: string }> {
  const res = await apiFetch(`${API_BASE_URL}/api/superadmin/companies/${companyId}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error ?? 'Failed to add user');
  return data.data;
}
